import memoize from 'lodash/memoize';
import isNumber from 'lodash/isNumber';
import BigNumber from 'bignumber.js';
import psl from 'psl';
import { getAnalytics, logEvent } from 'firebase/analytics';
import Cookies from 'js-cookie';
import { axiosClient, axiosNuxt } from '~/common/js/axios';
import { wait } from '~/common/js/utils';
import debounce from 'lodash/debounce';
import clone from 'lodash/clone';
import _isNil from 'lodash/isNil';
import { COOKIE_NAME } from './login-util';
import { DEFAULT_CURRENCY, CURRENCY_UNIT_MAP } from './const';
/**
 * 交易类型
 */
export enum TRADE_ENUMS {
  /**
   * 现货
   */
  SPOT,
  /**
   * U本位合约
   */
  CONTRACT_USDT,
  /**
   * 币本位合约
   */
  CONTRACT_COIN,
  /**
   * 杠杆
   */
  MARGIN,
}

export enum PROJECT {
  /**
   * 主站
   */
  MASTER = 0,
  /**
   * 合约
   */
  CONTRACT = 1,
}

export const createHostname = memoize((hostname: string) => {
  if (hostname === 'localhost' || /^[\d.]{1,}$/.test(hostname)) {
    return {
      master: location.origin,
      contract: location.origin,
    };
  }

  const result = psl.parse(hostname);
  const masterSegments = [result.domain];
  const contractSegments = [result.domain];
  if (['beta', 'derivatives-beta'].includes(result.subdomain)) {
    // beta 适配
    masterSegments.unshift('beta');
    contractSegments.unshift('derivatives-beta');
  } else if (['www', 'derivatives'].includes(result.subdomain)) {
    masterSegments.unshift('www');
    contractSegments.unshift('derivatives');
  } else {
    // 本地开发环境
    // eslint-disable-next-line no-lonely-if
    if (result.subdomain.includes('derivatives')) {
      masterSegments.unshift('www');
      contractSegments.unshift(result.subdomain);
    } else {
      masterSegments.unshift(result.subdomain);
      contractSegments.unshift('derivatives');
    }
  }
  let masterHostName = masterSegments.join('.');
  let contractHostName = contractSegments.join('.');
  if (masterHostName === hostname) {
    masterHostName = ``;
    contractHostName = `https://${contractHostName}`;
  } else if (contractHostName === hostname) {
    contractHostName = ``;
    masterHostName = `https://${masterHostName}`;
  }
  return {
    master: masterHostName,
    contract: contractHostName,
  };
});

export const createLink = (project: PROJECT, path: string) => {
  if (process.server) {
    return '';
  }
  const { master, contract } = createHostname(location.hostname);
  switch (project) {
    case PROJECT.MASTER:
      return `${master}${path}`;
    case PROJECT.CONTRACT:
      return `${contract}${path}`;
    default:
      return path;
  }
};

type validateAuthorizeOptionType = {
  callback?: () => void
}
// 需要授权的错误码
export const AUTHORIZE_ERROR_CODE: (string | number)[] = [33138, 34066]; // 34066 -> 理财拉起二维码错误码
export const verifyAuthorizeCode = (store: any, errorCode: number, options: validateAuthorizeOptionType) => {
  if (AUTHORIZE_ERROR_CODE.includes(errorCode)) {
    store.commit('VERIFY_KYC_FLOW_START', {
      startKycLevel: -1,
      callback: options.callback,
    });
    return false;
  }
  return true;
};

interface MessageConfig {
  level: 'success' | 'warning' | 'info' | 'error'
  message: string
  duration?: number
  throttleTime?: number
}

const MessageQueue = new Map<string, boolean>();

export const debounceMessage = (config: MessageConfig) => {
  const route = useRoute();
  // if (!window.$nuxt) {
  //   return;
  // }
  if (route.query?.jumpcodebm) return;
  if (MessageQueue.has(config.message)) {
    return;
  }
  setTimeout(
    () => {
      MessageQueue.delete(config.message);
    },
    isNumber(config.throttleTime) ? config.throttleTime : 5000
  );

  MessageQueue.set(config.message, true);
  ElMessage({
    message: config.message,
    type: config.level,
    duration: isNumber(config.duration) ? config.duration : 3000,
  });
};

type firebaseEventDataType = {
  [key: string]: any
}
export function firebaseLogEvent(name: string, eventData: firebaseEventDataType) {
  if (import.meta.env.VITE_DEBUG !== 'true' && process.client) {
    try {
      const analytics = getAnalytics();
      logEvent(analytics, name, eventData);
    } catch (e) {
      console.log(e);
    }
  }
}

export const openZendesk = async (url: string, isBlank = false, doRedirect = true) => {
  const oriArr = [
    'https://support.bitmart.com',
    'https://support.bmx.fund',
    'https://www.bitmart.com/html/user_zendesk_sso?redirect=',
    '/html/user_zendesk_sso?redirect=',
  ];
  let returnTo = url;
  oriArr.forEach((host) => {
    if (returnTo.startsWith(host)) {
      returnTo = returnTo.replace(host, '');
    }
  });
// TODO 3619
  const token = Cookies.get(COOKIE_NAME.SALT);
  let redirectUrl;
  if (token) {
    try {
      const res = await axiosClient({
        method: 'get',
        url: '/gw-api/user-center/token/zendesk',
        params: {
          returnTo,
        },
        cache: false,
      });
      if (res.success) {
        redirectUrl = res.data.redirectUrl;
      } else {
        // redirectUrl = `https://support.bitmart.com${returnTo}`
        redirectUrl = `${process.env.MASTER_HOST}/html/user_zendesk_sso?redirect=${encodeURIComponent(returnTo)}`;
      }
    } catch (err) {
      // console.log(err)
      redirectUrl = `${process.env.MASTER_HOST}/html/user_zendesk_sso?redirect=${encodeURIComponent(returnTo)}`;
    }
  } else {
    // 未登录
    redirectUrl = `https://support.bitmart.com${returnTo}`;
  }
  if (doRedirect) {
    if (isBlank) {
      window.open(redirectUrl);
    } else {
      location.href = redirectUrl;
    }
  }
  return redirectUrl;
};

interface IViewConfigObjectItem {
  parentKeys: Record<string, number>;
  useParentKeysConfig?: Record<string, number>;
	keys: Record<string, number>;
	useKeysConfig?: Record<string, number>;
  cache: any;
  fetch: Function;
  result?: {
    [key: string]: string;
  }
}
interface IViewConfigObject {
  [key:number|string]:IViewConfigObjectItem
}
const viewConfigObject: IViewConfigObject = {};
const customConfigObject: IViewConfigObject = {};

// 逻辑参考getViewConfigAxios，目前无法进行合并处理
const getCustomConfigAxios = async (configParentKeyItem: IViewConfigObjectItem) => {
	const parentKeys = Object.keys(configParentKeyItem.parentKeys);
	configParentKeyItem.useParentKeysConfig = clone(configParentKeyItem.parentKeys);
	configParentKeyItem.parentKeys = {};
	if (process.client) {
		const res = await axiosClient({
			method: 'POST',
			url: '/gw-api/content/front/lowCode/batchQueryData',
			data: {
				parentKeys,
			},
			headers: {
				'Content-Type': 'application/json',
			},
			cache: configParentKeyItem.cache,
		});
    if (res.success) {
		  configParentKeyItem.result = res.data.parentJsonMap;
    }
	} else {
		const res = await axiosNuxt({
			method: 'POST',
			url: '/content/front/lowCode/batchQueryData',
			data: {
				parentKeys,
			},
			headers: {
				'Content-Type': 'application/json',
			},
		});
    if (res.success) {
	  	configParentKeyItem.result = res.data?.parentJsonMap || {};
    }
	}
};

const getViewConfigAxios = async (configKeyItem:IViewConfigObjectItem) => {
  const keys = clone(Object.keys(configKeyItem.keys));
  configKeyItem.useKeysConfig=clone(configKeyItem.keys);
  configKeyItem.keys = [];
  if (process.client) {

    const res = await axiosClient({
      method: 'POST',
      url: '/gw-api/content/front/lowCode/batchQueryData',
      data: {
        keys,
      },
      headers: {
        'Content-Type': 'application/json',
      },
      cache: configKeyItem.cache,
    });
    if (res.success) {
      configKeyItem.result = res.data.jsonMap;
    }
    // setTimeout(() => delete configKeyItem.result, 40);
  } else {
    const res = await axiosNuxt({
      method: 'POST',
      url: '/content/front/lowCode/batchQueryData',
      data: {
        keys,
      },
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (res.success) {
      configKeyItem.result = res.data.jsonMap;
    }
    // setTimeout(() => delete configKeyItem.result, 40);
  }
};

/**
 * 用来获取配置项
 * @param {*} configKey key值
 */
export async function getCustomConfig(configParentKey: string, cacheTtl: boolean | number = 5 * 60 * 1e3) {
	if (!process.client) {
		cacheTtl = false;
	}
	try {
		let cache;
		if (cacheTtl) {
			cache = {
				ttl: cacheTtl,
				methods: ['get', 'post'],
				interpretHeader: false,
			};
		} else {
			cache = false;
		}
		if (!customConfigObject[cacheTtl + '']) {
			customConfigObject[cacheTtl + ''] = {
				parentKeys: {
					[configParentKey]: 1,
				},
				keys: {},
				cache,
				fetch: debounce(getCustomConfigAxios, 50),
			};
			customConfigObject[cacheTtl + ''].fetch(customConfigObject[cacheTtl + '']);
		} else if (!customConfigObject[cacheTtl + ''].parentKeys[configParentKey]) {
			customConfigObject[cacheTtl + ''].parentKeys[configParentKey] = 1;
			if (Object.keys(customConfigObject[cacheTtl + ''].parentKeys).length === 10) {
				// 批量查询接口限制最多10条，立刻触发
				getCustomConfigAxios(customConfigObject[cacheTtl + '']);
			} else {
				customConfigObject[cacheTtl + ''].fetch(customConfigObject[cacheTtl + '']);
			}
		} else if (customConfigObject[cacheTtl + ''].parentKeys[configParentKey]) {
			customConfigObject[cacheTtl + ''].parentKeys[configParentKey] += 1;
		}

		while (!customConfigObject[cacheTtl + ''].result) {
			await wait(10);
		}

		// 这里需要优化
		const res = customConfigObject[cacheTtl + '']?.result;

		if (res) {
			const customKey = Object.keys(res);
			const resData = customKey.reduce((map: Record<string, number>, key: string) => {
				// JSON解析失败时的处理 try catch
				try {
					map[key] = JSON.parse(res[key]);
				} catch (e) {
					console.log('e', e);
				}

				return map;
			}, {});

			if (customConfigObject[cacheTtl + '']) {
				customConfigObject[cacheTtl + ''].useParentKeysConfig[configParentKey] -= 1;
			}
			// if (customConfigObject[cacheTtl + '']?.useParentKeysConfig[configParentKey] === 0) {
			// 	delete customConfigObject[cacheTtl + '']?.result?.[configParentKey];
			// 	if (!Object.keys(customConfigObject[cacheTtl + '']?.result)?.length) {
			// 		delete customConfigObject[cacheTtl + ''];
			// 	}
			// }
			return resData;
		}
		return null;
	} catch (error) {
		console.error(error);
		return null;
	}
}

/**
 * 用来获取配置项
 * @param {*} configKey key值
 */
export async function getViewConfig(configKey:string, cacheTtl: boolean | number = 5 * 60 * 1e3) {
  if (!process.client) {
    cacheTtl = false;
  }
  try {
    let cache;
    if (cacheTtl) {
      cache = {
        ttl: cacheTtl,
        methods: ['get', 'post'],
        interpretHeader: false,
      };
    } else {
      cache = false;
    }
    if (!viewConfigObject[cacheTtl+'']) {
      viewConfigObject[cacheTtl+''] = {
        keys: {[configKey]:1},
        cache,
        fetch: debounce(getViewConfigAxios, 50),
      };
      viewConfigObject[cacheTtl+''].fetch(viewConfigObject[cacheTtl+'']);
    } else if (!viewConfigObject[cacheTtl+''].keys[configKey]) {
      viewConfigObject[cacheTtl+''].keys[configKey]=1;
      if (Object.keys(viewConfigObject[cacheTtl+''].keys).length === 10) {
        //批量查询接口限制最多10条
        getViewConfigAxios(viewConfigObject[cacheTtl+'']);
      } else {
        viewConfigObject[cacheTtl+''].fetch(viewConfigObject[cacheTtl+'']);
      }
    }else if(viewConfigObject[cacheTtl+''].keys[configKey]){
      viewConfigObject[cacheTtl+''].keys[configKey]+=1;
    }

    while (!viewConfigObject[cacheTtl+'']?.result) {
      await wait(10);
    }
    const res = viewConfigObject[cacheTtl+'']?.result?.[configKey];

    if (res) {
      const retData = JSON.parse(res);
      if (viewConfigObject[cacheTtl+'']) {
        viewConfigObject[cacheTtl+''].useKeysConfig[configKey]-=1;
      }
      if(viewConfigObject[cacheTtl+'']?.useKeysConfig[configKey]===0){
        delete viewConfigObject[cacheTtl+'']?.result?.[configKey];
        if(!Object.keys(viewConfigObject[cacheTtl+'']?.result)?.length){
          delete viewConfigObject[cacheTtl+''];
        }
      }
      return retData;
    }
    return null;
  } catch (error) {
    console.log(error);
    return null;
  }
}

export const parseUrl = (path: string, _: boolean) => {
  const newPath = `${location.origin}${path}`;
  const url = new URL(newPath);
  const query: any = {};
  url.searchParams.forEach((val: string, key: string) => {
    query[key] = val;
  });
  return {
    url,
    query
  };
};

export const ZENDESK_LANG_MAP = new Map([
  ['cn', 'zh-cn'],
  ['cn_tw', 'zh-tw'],
  ['en', 'en-us'],
  ['es', 'es'],
  ['jp', 'ja'],
  ['br', 'pt'],
  ['tr', 'tr'],
  ['vn', 'vi'],
  ['ru', 'ru'],
  ['nl-NL', 'nl'],
]);

export const breakDownHostUrl = (url: string) => {
  let hostUrl = url;
  let newUrl = hostUrl;
  // 防止未做301跳转，产生XXX.XXX的域名
  if (hostUrl.split('.') && hostUrl.split('.').length <= 2) {
    newUrl = hostUrl;
  } else {
    let number = hostUrl.indexOf('.');
    newUrl = hostUrl.slice(number + 1);
  }
  return newUrl;
};

const LOCAL_DOMAIN_MAP = new Map([
  ['production', 'bitmart.com'],
  ['testing', 'bitmartgcp-test.com'],
  ['dev', 'bitmart-dev.com'],
]);
const LEGACY_TESTING_DOMAIN = 'bitmarttest.com';


export const getAppDyamicHost=memoize(async ()=>{

  const bitMartAppPattern = /BitMart/;
  const isAppRuntime = bitMartAppPattern.test(navigator?.userAgent);
  if (isAppRuntime) {
    const diff = 100;
    let totalTime = 0;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (window.jsBridge && window.jsBridge.getDomainData) {
        break;
      } else {
        if (totalTime >= 1500) {
          console.log('no getDomainData');
          break;
        }
        await wait(diff);
        totalTime += diff;
      }
    }
  }
  return new Promise((resolve)=>{

    if (window.jsBridge && window.jsBridge.getDomainData) {
      console.log('getDomainData');
      window.jsBridge.getDomainData((res)=>{
        try {
          res=JSON.parse(res);
          
        } catch (_error) {
        res = res.substring(15,res.length-2);
        res=JSON.parse(res);
          
        }
          
          if (typeof res.domainData==='string') {
            res=JSON.parse(res.domainData);
          }

          if (typeof res?.domain_list==='string') {
            res=JSON.parse(res.domain_list);
          }
        

          const {domain_list}=res;   
          const domainsMap:any={};
          const domainObj=domain_list.find((item)=>{
            return Object.keys(item).find((key)=>{
              const {host}=item[key];
             return process.env.DERIVATIVES_HOST?.includes(host)||process.env.MASTER_HOST?.includes(host);
            });
          })||domain_list?.[0];

          if (domainObj) {
            Object.keys(domainObj).forEach((key)=>{
              const {host}=domainObj[key];
            if (host.length) {
              domainsMap[key]=host;
            }
          });
          }

          resolve(domainsMap);
        });
    }
  });
});

export const getCurHost = memoize((origin: string) => {
  let url;
  let dynamicHost:any;
  let isInApp;

  // 当前域名是否为合约域名
  const isDerivatives = process.env.PROJECT_NAME==='DERIVATIVES';
  if (process.client) {
    isInApp = (/BitMart/).test(navigator?.userAgent);
    getAppDyamicHost().then((res)=>{
      dynamicHost=res;
      console.log('getAppDyamicHost', res);
    });
    const domainPattern=/(bitmartgcp-(dev|test)|bitmart)+\.(com|cool)$/;

    if (!isInApp&&!domainPattern.test(location.host)&&process.env.NODE_ENV != 'development') {
      if (isDerivatives) {
        location.href=location.href.replace(location.origin,process.env.DERIVATIVES_HOST as string);
      }else{
        location.href=location.href.replace(location.origin,process.env.MASTER_HOST as string);
      }
    }
  }
  try {
    if (isInApp) {
      if (isDerivatives) {
        url = new URL(process.env.DERIVATIVES_HOST as string);
      }else{
        url = new URL(process.env.MASTER_HOST as string);
      }
    }else{
      url = new URL(origin);
    }
  } catch (error) {
    console.log(111, error);
  }
  // console.log('domain_env', process.env.domain_env);
  const isLocal = process.env.NODE_ENV === 'development';
  const isTest = process.env.domain_env === 'testing';

  // console.log('url', url);
  let endFix, isBeta, protocol = 'https:', hostFix='com';
  if (url) {
    endFix = breakDownHostUrl(url.host); // bitmartgcp-test.com / bitmartgcp-test.cool / bitmart.com / bitmart.cool
    isBeta = url.hostname.includes('beta.');
    protocol = url.protocol;
  } else {
    endFix = 'bitmart.com';
    isBeta = false;
  }
  const hostFixArr = endFix.split('.');
  hostFix = hostFixArr[hostFixArr.length - 1];
  let contractEndFix = endFix;
  // console.log('contractEndFix', contractEndFix);

  if (isLocal) {
    endFix = LOCAL_DOMAIN_MAP.get(process.env.domain_env as string) as string;
    if (isTest) {
      contractEndFix = LEGACY_TESTING_DOMAIN;
    } else {
      contractEndFix = endFix;
    }
    protocol = 'https:';
  } else if (isTest) {
    contractEndFix = `bitmarttest.${hostFix}`;
  }

  let siteOrigin;
  if (isBeta) {
    siteOrigin = `${protocol}//beta.${endFix}`; // 主站的 地址
  } else {
    siteOrigin = `${protocol}//www.${endFix}`; // 主站的 地址
  }

  let derivativesOrigin;
  if (isBeta) {
    derivativesOrigin = siteOrigin.replace('beta.', 'derivatives-beta.');
  } else {
    derivativesOrigin = siteOrigin.replace('www.', 'derivatives.');
  }
  const cookieDomain = `.${endFix}`;

  let wssHostV2;
  let contractApiHostV2;
  let contractApiHost;
  let wssHost;
  let wsUrl;
  if (isTest) {
    // wssHostV2 = 'wss://'+dynamicHost.get('contract-ws-sandbox.bitmarttest.com');
    // contractApiHostV2 = `${protocol}//${dynamicHost.get('contract-sandbox.bitmarttest.com')}`;
    wssHostV2 = `wss://contract-ws-sandbox.${contractEndFix}`;
    contractApiHostV2 = `${protocol}//contract-sandbox.bitmarttest.com`;

    contractApiHost = `${protocol}//contract.${contractEndFix}`;
    wssHost = `wss://contractapp.${contractEndFix}`;
    wsUrl ='wss://ws-manager-compress.bitmartgcp-test.com';
  } else {
    wssHostV2 = `wss://contract-ws-v2.${contractEndFix}`;
    contractApiHostV2 = `${protocol}//contract-v2.${contractEndFix}`;
    contractApiHost = `${protocol}//contract.${contractEndFix}`;
    wssHost = `wss://contractapp.${contractEndFix}`;
    wsUrl ='wss://ws-manager-compress-web.bitmart.com';
  }

  // 所有的域名集合
  const allOrigin = [...new Set(
    [
      siteOrigin,
      siteOrigin?.replace('.com', '.cool'),
      siteOrigin?.replace('.cool', '.com'),
    ]
  )];

  const findUrlStart = (url: string) => allOrigin.find(() => url.startsWith(origin));


  const siteConfig = {
    origin: siteOrigin,
    derivativesOrigin, // https://derivatives.bitmart.com
    cookieDomain, // .bitmart.com
    isBeta,
    isDerivatives,
    contractApiHost,
    contractApiHostV2,
    wssHost,
    wssHostV2,
    endFix,
    wsUrl,
    allOrigin,
    findUrlStart,
    isInApp,
    sentryHost:process.env.SENTRY_DSN||'',
    imgCdnHost:process.env.IMG_CDN_PREFIX
  };
  if (process.env.ENV !== 'production' && process.env.NODE_ENV === 'development') {
    console.log('getCurHost', siteConfig);
  }
  const ob={...siteConfig};
  Object.defineProperty(ob, 'wssHostV2', {
    get() {
      console.log('in wssHost',dynamicHost);
        if (dynamicHost?.['contract-ws-sandbox.bitmarttest.com']) {
          return 'https://'+dynamicHost?.['contract-ws-sandbox.bitmart.com'];
        }else if (dynamicHost?.['contract-ws-v2.bitmarttest.com']) {
          return 'https://'+dynamicHost?.['contract-ws-v2.bitmart.com'];
        }else{
          return siteConfig.wssHostV2;
        }
    },
  });
  Object.defineProperty(ob, 'wsUrl', {
    get() {
      console.log('in wsUrl',dynamicHost);
        if (dynamicHost?.['ws-manager-compress.bitmartgcp-test.com']) {
          return 'wss://'+dynamicHost?.['ws-manager-compress.bitmartgcp-test.com'];
        }else if (dynamicHost?.['ws-manager-compress-web.bitmart.com']) {
          return 'wss://'+dynamicHost?.['ws-manager-compress-web.bitmart.com'];
        }else{
          return siteConfig.wsUrl;
        }
    },
  });
  Object.defineProperty(ob, 'contractApiHostV2', {
    get() {
      console.log('in contractApiHostV2',dynamicHost);
        if (dynamicHost?.['contract-sandbox.bitmarttest.com']) {
          return 'https://'+dynamicHost?.['contract-sandbox.bitmarttest.com'];
        }else if (dynamicHost?.['contract-v2.bitmart.com']) {
          return 'https://'+dynamicHost?.['contract-v2.bitmart.com'];
        }else{
          return siteConfig.contractApiHostV2;
        }
    },
  });
  Object.defineProperty(ob, 'sentryHost', {
    get() {
      console.log('in sentryHost',dynamicHost);
        if (dynamicHost?.['sentryingest.bitmart.com']) {
          return siteConfig.sentryHost?.replace('sentryingest.bitmart.com',dynamicHost?.['sentryingest.bitmart.com']);
        }else{
          return siteConfig.sentryHost;
        }
    },
  });
  Object.defineProperty(ob, 'imgCdnHost', {
    get() {
      console.log('in imgCdnHost',dynamicHost);
        if (dynamicHost?.['img.bitmart.com']) {
          return siteConfig.imgCdnHost?.replace('img.bitmart.com',dynamicHost?.['img.bitmart.com']);
        }else{
          return siteConfig.imgCdnHost;
        }
    },
  });
  return ob;
});
/**
 * @description 科学计数法转为string
 * @param {string, number} param
 */
export function scientificNumber (param) {
  let strParam = String(param);
  // 是否含有科学计数
  const flag = /e/.test(strParam);
  // 正数负数的标记
  let sign = '';
  if (!flag) {return param;}
  // 是负数，则先去除前面的负号
  if (strParam.startsWith('-')) {
    strParam = strParam.replace('-', '');
    sign = '-';
  }
  // 指数符号 true: 正，false: 负
  let sysbol = true;
  if (/e-/.test(strParam)) {
    sysbol = false;
  }
  // 指数
  const index = Number(strParam.match(/\d+$/)?.[0]);
  // 基数
  // eslint-disable-next-line no-useless-escape
  const basis = strParam.match(/^[\d\.]+/)?.[0].replace(/\./, '') || '';

  if (sysbol) {
    return sign + basis.padEnd(index + 1, '0');
  } else {
    return sign + basis.padStart(index + basis.length, '0').replace(/^0/, '0.');
  }
}

interface IFormatNumberOptions {
  mantissa?: number;           // 小数精度, 默认为2
  mantissaForZero?: number;    // 为0的小数精度, 默认为null
  mantissaForInteger?: number; // 为整数的小数精度, 默认为null
  fillZero?: boolean;          // 是否补零，默认为true
  thousandSeparated?: boolean; // 是否使用千分位分隔符，默认为true
  thousandSepStart?: number;   // 大于该数值才开始使用千分位分隔符，默认为0
  percentage?: boolean;        // 是否输出百分比，默认为false
  forceSign?: boolean;         // 是否输出符号，默认为false
  ellipseCount?: number;       // 小数点后连续n个0的省略，默认为 0 不省略
  integerUnit?: 'k' | 'm' | 'b' | 't' | 'auto' | false; // 整数部分单位，默认为false
  keepTailTwo?: boolean;       // 数字大于0小于1时，是否保留两位末尾非零数字（适用于金额场景），默认为false
  inputMode?: boolean;          // 输入模式，只处理符号，千分位，精度，默认为false
}

/**
 * 格式化数字，支持指定小数精度、千分位、百分比和符号。
 *
 * @param {number | string} number - 要格式化的数字。
 * @param {IFormatNumberOptions} [options] - 可选的格式化选项，参考IFormatNumberOptions
 * @returns {string} 格式化后的数字字符串。
 * @example formatNumberString(1234567.89) => '1,234,567.89'
 */
export const formatNumberString = (number: number | string, options: IFormatNumberOptions = {}): string => {
  let {
    mantissa = 2,
    mantissaForZero = null,
    mantissaForInteger = null,
    fillZero = true,
    thousandSeparated = true,
    thousandSepStart = 0,
    percentage = false,
    forceSign = false,
    ellipseCount = 0,
    integerUnit = false,
    keepTailTwo = false,
    inputMode = false,
  } = options;
  const originalNumber = scientificNumber(number);
  const hasPercentage = String(number).includes('%');
  const pointIndex = String(number).indexOf('.');
  const lastIsPoint = pointIndex === String(number).length - 1;
  const isInteger = /^-?\d+$/.test(String(number));

  // 处理null，undefined，'0'等情况
  if (_isNil(number) || Number(number) === 0) {
    number = 0;
  }
  // 非纯数字直接返回，比如已经包含有,--等
  if (Number.isNaN(Number(number)) && !hasPercentage) {
    return String(number);
  }

  // 兼容科学计数法
  number = scientificNumber(number);

  // 百分比处理
  if (percentage) {
    if (hasPercentage) {
      number = BigNumber(String(number).replace('%', '')).toFixed();
    } else {
      number = BigNumber(number).multipliedBy(100).toFixed();
    }
  } else {
    number = BigNumber(number).toFixed();

    // 处理保留两位非零数字
    if (keepTailTwo && Number(number) > 0 && Number(number) < 1) {
      number = number.replace(/(^0\.0*[1-9]\d?)\d*$/, '$1');
      mantissa = Number.MAX_SAFE_INTEGER;
      fillZero = false;
    }
  }

  // KMBT单位处理，单位字符小写
  let unitSuffix = '';
  if (integerUnit) {
    const unitMap = {
      k: 3,
      m: 6,
      b: 9,
      t: 12,
    };
    const unit = unitMap[integerUnit];
    if (unit) {
      unitSuffix = integerUnit;
      number = BigNumber(number).dividedBy(10 ** unit).toFixed();
    } else if (integerUnit === 'auto') {
      // auto参考了numbro的average配置的实现
      // https://github.com/BenjaminVanRyseghem/numbro/blob/develop/src/formatting.js#L363
      const absNumber = Math.abs(Number(number));
      if (absNumber >= 1e12) {
        unitSuffix = 't';
        number = BigNumber(number).dividedBy(10 ** unitMap.t).toFixed();
      } else if (absNumber >= 1e9) {
        unitSuffix = 'b';
        number = BigNumber(number).dividedBy(10 ** unitMap.b).toFixed();
      } else if (absNumber >= 1e6) {
        unitSuffix = 'm';
        number = BigNumber(number).dividedBy(10 ** unitMap.m).toFixed();
      } else if (absNumber >= 1e3) {
        unitSuffix = 'k';
        number = BigNumber(number).dividedBy(10 ** unitMap.k).toFixed();
      }
    }
  }

  let result = String(inputMode ? originalNumber : number);

  // 符号处理
  if (forceSign && !result.startsWith('-') && !result.startsWith('+')) {
    result = '+' + result;
  }

  // 千分位处理
  if (thousandSeparated && Number(result) >= thousandSepStart) {
    result = result.replace(/^(\d+)(?=\.\d+)?/, (match) => {
      return match.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    });
  } else if (thousandSeparated === false) {
    result = result.replace(/,/g, '');
  }

  // 分为整数部分和小数部分
  let [integerPart, decimalPart = ''] = result.split('.');

  if (Number(result) === 0 && mantissaForZero !== null && mantissaForZero >= 0) {
    // 为0的小数精度展示处理
    mantissa = mantissaForZero;
  } else if (isInteger && mantissaForInteger !== null && mantissaForInteger >= 0) {
    // 纯整数精度展示处理
    mantissa = mantissaForInteger;
  }

  // 小数精度处理
  if (isNumber(mantissa) && mantissa >= 0) {
    const decimalLen = decimalPart.length;
    if (decimalLen < mantissa && fillZero) {
      const arr = new Array(mantissa - decimalLen).fill('0');
      decimalPart = decimalPart + arr.join('');
    } else if (decimalLen > mantissa) {
      decimalPart = decimalPart.slice(0, mantissa);
    }
    if (decimalPart && ellipseCount > 0 && decimalLen > ellipseCount) {
      // 动态匹配0的个数
      const ellipseRegex = new RegExp(`^(0{${ellipseCount},})`);
      const match = decimalPart.match(ellipseRegex);
      if (match) {
        const len = match[1].length;
        decimalPart = `0{${len}}${decimalPart.slice(len)}`;
      }
    }
    result = decimalPart || (lastIsPoint && mantissa > 0) ? `${integerPart}.${decimalPart}` : integerPart;
  }

  if (inputMode) {
    return result;
  }

  if (unitSuffix) {
    result = result + unitSuffix;
  }

  if (percentage) {
    result = result + '%';
  }

  return result;
};


// currency法币的符号对应关系
export const getCurrencySymbol = (currency = DEFAULT_CURRENCY) => {
  return CURRENCY_UNIT_MAP[currency] as any;
};

// 新增加，获取当前法币汇率兑美元汇率
export function getExchangeRateByCurrency(currency = DEFAULT_CURRENCY, store: any) {
  const currStore = store;
  return currStore.state.exchangeRate[currency];
}

/**
 * 千位计数法转换，小于100000直接返回，大于100000满1000进一位(K,M,B,T,P,E,Z,Y)
 * @param {*} num 数字
 * @param {*} dp 小数位数
 * @param {*} needThousand 是否需要千分位
 * @returns 转换后的字符串
 */
export function thousandCount(options: { num: number | string; dp?: number | null; needThousand: boolean }) {
  const { num, dp = 2, needThousand = false } = options;

  const formatNum = Number(num);
  if (formatNum < 100000) {
    if (needThousand) {
      const [int, decimal] = String(formatNum.toFixed(dp)).split('.');
      return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${decimal ? `.${decimal}` : ''}`;
    } else {
      return formatNum.toFixed(dp);
    }
  }
  const arr = ['K', 'M', 'B', 'T', 'P', 'E', 'Z', 'Y'];
  let index = 0;
  let result = formatNum;
  while (result >= 1000) {
    result = result / 1000;
    index++;
  }
  return result.toFixed(dp) + arr[index - 1];
}

/**
 * 小精度金额格式化，主要作用是合并小数点后0的位数，例如0.000001格式化为0.0{5}1
 * @param {number | string} amount 需要格式化的金额/数字
 * @param {number | undefined} precision 精度，用于保留小数位数，不传则不修改小数位数
 * @returns {string} 返回格式化后的金额/数字
 */
export const convertAmountFormatZero = (amount: number | string, precision?: number) => {
  const bigAmount = new BigNumber(amount);
  if (bigAmount.isGreaterThanOrEqualTo(0.00001)) {
    return bigAmount.toFixed(precision, BigNumber.ROUND_DOWN);
  } else {
    const reg = /^-?0\.(0{4,})/;
    const strAmount = bigAmount.toFixed(precision, BigNumber.ROUND_DOWN);
    // 获取match到的值
    const match = strAmount.match(reg);
    if (match) {
      const len = match[1].length;
      const startLen = bigAmount.isNegative() ? 3 : 2;
      return `${bigAmount.isNegative() ? '-' : ''}0.0{${len}}${strAmount.slice(startLen + len)}`;
    } else {
      return bigAmount.toFixed(precision, BigNumber.ROUND_DOWN);
    }
  }
};

interface ExchangeCurrencyOptions {
  amount?: string | number;
  store?: any;
  currency?: string;
  space?: boolean;
  needUnit?: boolean;
  unitBefore?: boolean;
  needThousand?: boolean;
  formatThousandth?: boolean;
  dynamicDp?: boolean;
  fillZero?: number;
  needSign?: boolean;
}

interface ExchangeCurrencyByCoinOptions extends ExchangeCurrencyOptions {
  usdAmount?: string;
  coin?: string;
  coinRate?: number;
  formatThousandth?: boolean;
  dynamicDp?: boolean;
  formatZero?: boolean;
}

/**
 * 根据美元转换为当前语言的金额
 * @param {object} opts 配置项
 * @param {number} opts.amount - 美元金额
 * @param {object} opts.store store，如果传了store就从传入参数里拿，如果没传会去window中找，所以服务端渲染时务必传store
 * @param {string} opts.currency 指定法币单位
 * @param {boolean} opts.space 是否需要空格，默认是需要的，如$ 100, 100 USD
 * @param {boolean} opts.needUnit 是否需要单位，默认是需要的
 * @param {boolean} opts.unitBefore 如果需要单位，是否在前面加符号单位，默认是在前面，如$100，如果是false，就是100 USD
 * @param {boolean} opts.needThousand 是否需要千分位，默认是不需要的
 * @param {boolean} opts.formatThousandth 是否需要格式化为千位计数法，默认是不需要的，注意千分位计数法和千分位符号是不一样的，千分位计数法是数字后加BMT，千分位符号是每三位一个逗号
 * @param {boolean} opts.dynamicDp 是否需要动态精度，默认是不需要的，默认保留2位小数，如果需要，小数点后的位数会根据传入的金额来决定，如果金额是1.234，小数点后的位数就是3，如果金额是1.2，小数点后的位数就是1
 * @param {boolean} opts.fillZero 是否需要对小数点后值用0填充补足到指定长度，默认为0，即不需要补足，设置为2时，如果该值为2，传入的amount=1.2时则补足为1.20
 * @param {boolean} opts.needSign 是否添加正负号，默认是不需要的
 * @author inm
 * @returns 转换后的金额，默认是字符串类型，因为有可能带上单位符号
 */
// lisfan注：过渡期，先保持原来的convertAmount逻辑不变，避免有部分页面未改动到而出错
// 名字变的不一样后，后期好替换
export function exchangeCurrency(options: ExchangeCurrencyOptions) {
  let {
    amount,
    store,
    currency,
    space = true,
    needUnit = true,
    unitBefore = true,
    needThousand = false,
    formatThousandth = false,
    dynamicDp = false,
    fillZero = 0,
    needSign = false,
  } = options;

  const currStore = store;

  if (!currStore) return !fillZero ? '0' : `0.${'0'.repeat(fillZero)}`;

  // 非0处理
  // 指定currency或使用当前系统全局的currency
  const currCurrency = currency || currStore.state.currency;

  // 提供默认值
  if (!amount) {
    const filledZero = !fillZero ? '0' : `0.${'0'.repeat(fillZero)}`;
    if (!needUnit) return filledZero;

    return unitBefore
      ? `${getCurrencySymbol(currCurrency)}${space ? ' ' : ''}${filledZero}`
      : `${filledZero}${space ? ' ' : ''}${currCurrency || DEFAULT_CURRENCY}`;
  }

  const calAmount = BigNumber(amount);
  const sign = calAmount.s;
  const dp = dynamicDp ? ((calAmount.dp() || 0) < 2 ? 2 : calAmount.dp()) : 2;
  // 填充小数位0的长度
  // 如果存在fillZero，则不使用动态精度
  const fixedPrecision = fillZero > (dp || 0) ? fillZero : dp;

  // 这里更新了计算方式，原来是金额 * 汇率，现在是金额 / 汇率，因为汇率系统是1法币对应多少个美元
  const formatPrice = calAmount.div(getExchangeRateByCurrency(currCurrency, currStore));
  // 此处允许传入null，
  let finalPrice = _isNil(fixedPrecision) ? formatPrice.valueOf() : formatPrice.toFixed(fixedPrecision);

  if (formatThousandth) {
    // 千位计数法
    finalPrice = thousandCount({
      num: finalPrice,
      dp: fixedPrecision,
      needThousand,
    });
  } else if (needThousand) {
    // 整数进行千分位，如果是小数，对整数部分进行千分位
    const [int, decimal] = finalPrice.split('.');
    finalPrice = `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${decimal ? `.${decimal}` : ''}`;
  }

  // 是否需要正负号
  if (needSign) {
    finalPrice = sign === 1 ? `+${finalPrice}` : finalPrice;
  }

  // 如果不需要单位
  if (!needUnit) {
    return finalPrice;
  }

  // 如果需要单位，且在前面
  if (unitBefore) {
    return `${getCurrencySymbol(currCurrency)}${space ? ' ' : ''}${finalPrice}`;
  } else {
    return `${finalPrice}${space ? ' ' : ''}${currCurrency || DEFAULT_CURRENCY}`;
  }
}

/**
 * 根据计价币价格转换为当前语言的金额，这个方法跟上面的方法的区别是，这个方法是使用计价币价格来直接计算
 * 小数点保留根据下面的规则展示
 * 1. 折算法币价格≥1，精度取2位小数
 * 2. 折算法币价格＜1，小数精度保证最后2位有数字，举例0.0000000001234，截取为0.00000000012，0.01234，截取为0.012
 * @param {object} opts 配置项
 * @param {number} opts.amount - 计价币金额
 * @param {object} opts.store store，如果传了store就从传入参数里拿，如果没传会去window中找，所以服务端渲染时务必传store
 * @param {string} opts.currency 指定法币单位
 * @param {string} opts.coin - 计价币种，如USDT，与以下2个参数都是exchangeCurrencyByCoin特有
 * @param {string} opts.coinRate - 计价币种 兑美元 汇率，未提供此值时，将通过opts.coin去全币种列表查询
 * @param {string} opts.usdAmount - 美元价格，什么时候会使用它：如果计价币接口或者计价币汇率接口出问题，就会使用它做backup方案
 * @param {boolean} opts.space 是否需要空格，默认是需要的，如$ 100, 100 USD
 * @param {boolean} opts.needUnit 是否需要单位，默认是需要的
 * @param {boolean} opts.unitBefore 如果需要单位，是否在前面加符号单位，默认是在前面，如$100，如果是false，就是100 USD
 * @param {boolean} opts.needThousand 是否需要千分位，默认是不需要的
 * @param {boolean} opts.formatThousandth 是否需要格式化为千位计数法，默认是不需要的，注意千分位计数法和千分位符号是不一样的，千分位计数法是数字后加BMT，千分位符号是每三位一个逗号
 * @param {boolean} opts.formatZero 是否需要对小数点后的0进行合并展示，合并的效果例如0.0{5}12，默认是不需要合并的
 * @param {boolean} opts.dynamicDp 是否需要动态精度，默认是需要的(区别于exchangeCurrency)，默认保留2位小数，如果需要，小数点后的位数会根据传入的金额来决定，如果金额是1.234，小数点后的位数就是3，如果金额是1.2，小数点后的位数就是1
 * @param {boolean} opts.fillZero 是否需要对小数点后值用0填充补足到指定长度，默认为0，即不需要补足，设置为2时，如果该值为2，传入的amount=1.2时则补足为1.20
 * @param {boolean} opts.needSign 是否添加正负号，默认是不需要的
 *
 * @author inm
 * @returns 转换后的金额，默认是字符串类型，因为有可能带上单位符号
 */
// lisfan注：过渡期，先保持原来的convertAmountByCoin逻辑不变，避免有部分页面未改动到而出错
// 名字变的不一样后，后期好替换
export function exchangeCurrencyByCoin(options: ExchangeCurrencyByCoinOptions) {
  const {
    amount,
    store,
    currency,
    needUnit = true,
    space = true,
    needThousand = false,
    unitBefore = true,
    usdAmount = '',
    coin = '',
    coinRate = undefined,
    formatThousandth = false,
    dynamicDp = true,
    formatZero = false,
    fillZero = 0,
    needSign = false,
  } = options;

  const currStore = store;

  if (!currStore) return !fillZero ? '0' : `0.${'0'.repeat(fillZero)}`;

  // 指定currency或使用当前系统全局的currency
  const currCurrency = currency || currStore.state.currency;
  // 获取计价币对美元的汇率
  const rate = coinRate || currStore.state?.marketCoinRateMap[coin]?.rate;
  const currencyRate = getExchangeRateByCurrency(currCurrency, currStore);

  // 提供默认值
  if (!amount || (!rate && !usdAmount)) {
    const filledZero = !fillZero ? '0' : `0.${'0'.repeat(fillZero)}`;
    if (!needUnit) return filledZero;

    return unitBefore
      ? `${getCurrencySymbol(currCurrency)}${space ? ' ' : ''}${filledZero}`
      : `${filledZero}${space ? ' ' : ''}${currCurrency || DEFAULT_CURRENCY}`;
  }

  let formatPrice;

  // 如果没有计价币对美元的汇率值，那就直接使用美元价格，如果美元价格也没有，则直接返回0
  if (!rate) {
    formatPrice = BigNumber(usdAmount).div(currencyRate);
  } else {
    formatPrice = BigNumber(amount).times(rate).div(currencyRate);
  }

  const sign = formatPrice.s;
  const dp = dynamicDp ? ((formatPrice.dp() || 0) < 2 ? 2 : formatPrice.dp()) : 2;
  // 填充小数位0的长度
  // 如果存在fillZero，则不使用动态精度
  const fixedPrecision = fillZero > (dp || 0) ? fillZero : dp;

  let finalPrice;

  /** 处理精度问题 - start */
  // 如果大于1，直接保留2位小数
  if (formatPrice.gte(1)) {
    finalPrice = formatPrice.toFixed(2);
    if (formatThousandth) {
      // 千位计数法
      finalPrice = thousandCount({
        num: finalPrice,
        needThousand,
      });
    } else if (needThousand) {
      // 整数进行千分位，如果是小数，对整数部分进行千分位
      const [int, decimal] = finalPrice.split('.');
      finalPrice = `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${decimal ? `.${decimal}` : ''}`;
    }
  } else if (formatPrice.isEqualTo(0)) {
    // 如果等于0，直接返回0, 这里返回字符串是为了之后做格式化
    finalPrice = '0';
  } else {
    // 小于1时，
    // 小数精度保证最后2位有数字，举例0.0000000001234，截取为0.00000000012
    // 按精度处理动态精度
    finalPrice = _isNil(fixedPrecision) ? formatPrice.valueOf() : formatPrice.toFixed(fixedPrecision); // 此处允许传入null，即不起作用
    // lisfan 2024.11.15 这一步会强制只保留两位有效数字，导致像dynamicDp和fillZero无效（先保持此效果不变)
    finalPrice = finalPrice.replace(/(^0\.0*[1-9]\d?)\d*$/, '$1'); // 只保留2位有效数字

    /** update 2024.06.05 支持小数点后多位0格式化 */
    if (formatZero) {
      finalPrice = convertAmountFormatZero(finalPrice);
    }
  }

  /** 处理精度问题 - end */

  // 是否需要正负号
  if (needSign) {
    finalPrice = sign === 1 ? `+${finalPrice}` : finalPrice;
  }

  // 如果不需要单位
  if (!needUnit) {
    return finalPrice;
  }

  // 如果需要单位，且在前面
  if (unitBefore) {
    return `${getCurrencySymbol(currCurrency)}${space ? ' ' : ''}${finalPrice}`;
  } else {
    return `${finalPrice}${space ? ' ' : ''}${currCurrency || DEFAULT_CURRENCY}`;
  }
}
